import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api";

// Phase 85 — PAG Talk : poste téléphonique navigateur (Twilio Voice SDK). Gère le jeton, la présence,
// les appels entrants (accept/refus), sortants (numéro, poste interne, file d'attente), micro et clavier DTMF.
export function useSoftphone({ enabled = true } = {}) {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const [state, setState] = useState("offline"); // offline | connecting | ready | incoming | calling | in-call | error | unavailable
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [active, setActive] = useState(null);
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const presence = useCallback((online) => api.post("/talk/presence", { online }).catch(() => {}), []);

  const bindCall = useCallback((call, meta) => {
    callRef.current = call;
    setActive({ ...meta, startedAt: Date.now() });
    setSeconds(0);
    setMuted(false);
    call.on("accept", () => { setState("in-call"); setActive((a) => (a ? { ...a, startedAt: Date.now() } : a)); });
    call.on("disconnect", () => { callRef.current = null; setActive(null); setIncoming(null); setState("ready"); });
    call.on("cancel", () => { callRef.current = null; setActive(null); setIncoming(null); setState("ready"); });
    call.on("reject", () => { callRef.current = null; setActive(null); setIncoming(null); setState("ready"); });
    call.on("error", (e) => { setError(e?.message || "Erreur d'appel"); callRef.current = null; setActive(null); setState("ready"); });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let hb;
    (async () => {
      setState("connecting");
      try {
        const { data } = await api.post("/talk/token");
        if (!alive) return;
        setInfo(data);
        const { Device } = await import("@twilio/voice-sdk");
        const device = new Device(data.token, { logLevel: 4, codecPreferences: ["opus", "pcmu"], closeProtection: true });
        deviceRef.current = device;
        device.on("registered", () => { setState("ready"); presence(true); });
        device.on("unregistered", () => setState("offline"));
        device.on("error", (e) => { setError(e?.message || "Erreur du poste"); setState("error"); });
        device.on("tokenWillExpire", async () => { try { const { data: d } = await api.post("/talk/token"); device.updateToken(d.token); } catch { /* ignore */ } });
        device.on("incoming", (call) => {
          const p = call.customParameters || new Map();
          const meta = { direction: "inbound", number: call.parameters?.From || "", name: p.get("callerName") || "", entreprise: p.get("entreprise") || "", route: p.get("route") || "" };
          setIncoming({ call, ...meta });
          setState("incoming");
          call.on("cancel", () => { setIncoming(null); setState("ready"); });
          call.on("disconnect", () => { setIncoming(null); callRef.current = null; setActive(null); setState("ready"); });
        });
        await device.register();
        hb = setInterval(() => presence(true), 45000);
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        setError(e?.response?.data?.detail || e?.message || "Poste indisponible");
        setState(status === 503 || status === 409 || status === 403 ? "unavailable" : "error");
      }
    })();
    return () => {
      alive = false;
      if (hb) clearInterval(hb);
      presence(false);
      try { deviceRef.current?.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
    };
  }, [enabled, presence]);

  useEffect(() => {
    if (state !== "in-call") return undefined;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  const accept = useCallback(() => {
    if (!incoming?.call) return;
    const { call, ...meta } = incoming;
    bindCall(call, meta);
    call.accept();
    setIncoming(null);
  }, [incoming, bindCall]);

  const reject = useCallback(() => { incoming?.call?.reject(); setIncoming(null); setState("ready"); }, [incoming]);

  const dial = useCallback(async (to, meta = {}) => {
    const device = deviceRef.current;
    if (!device || !to) return;
    setError("");
    setState("calling");
    const call = await device.connect({ params: { To: to } });
    bindCall(call, { direction: "outbound", number: to, ...meta });
  }, [bindCall]);

  const hangup = useCallback(() => { callRef.current?.disconnect(); deviceRef.current?.disconnectAll(); }, []);
  const toggleMute = useCallback(() => { const next = !muted; callRef.current?.mute(next); setMuted(next); }, [muted]);
  const sendDigits = useCallback((d) => callRef.current?.sendDigits(d), []);

  return { state, error, info, incoming, active, muted, seconds, accept, reject, dial, hangup, toggleMute, sendDigits };
}
